import { io, Socket } from "socket.io-client";
import Swal from "sweetalert2";
import {
  validateAddToCart,
  validateFavoriteProduct,
  validateFullPlacedBid,
  validateLatestBid,
  validateNewAuctionStatus,
  validateNewBidsCount,
  validateNewReward,
  validateWonAuction
} from "../utilities/responseValidators";
import {
  checkTimeSync,
  getUserLanguage,
  retrieveDeviceId,
  systemErrorAlertText,
  utilLogOut
} from "../utilities/utils";

class SocketController {
  private static socket: Socket;
  private readonly serverUrl: string;
  private socketStatus: "connected" | "connecting" | "disconnected" =
    "disconnected";
  private handlers: { [key: string]: (result: any) => void } = {};
  private readonly MAX_RECONNECT_ATTEMPTS = 100;
  private reconnectAttempts = 0;
  private readonly TIMEOUT_DURATION = 30000; // 30 seconds

  private userNickName: string | null = null;
  private userBidToken: string | null = null;
  private deviceId: string | null = null;

  constructor() {
    this.serverUrl = process.env.REACT_APP_SOCKET_URL || "";
    if (this.serverUrl === "") {
      Swal.fire({
        icon: "error",
        title: `Invalid Server URL`,
        html:
          `<strong class="font-extrabold">Please contact support</strong><br /><br />Server URL is empty` +
          systemErrorAlertText(),
        showConfirmButton: true,
        customClass: {
          popup: "!rounded-lg",
          confirmButton:
            "!bg-secondary !rounded-full !outline-none !border-none !shadow-none"
        }
      });
      console.error("Server URL is empty");
    }

    SocketController.socket = io(this.serverUrl, {
      // timeout: 60000,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      auth: {
        deviceId: retrieveDeviceId()
      }
    });
    this.connect();
    this.initializeListeners();
  }

  private initializeListeners() {
    SocketController.socket.on("messages", (response: SocketCom) => {
      // console.log("Received message:", response);
      if (response.command && this.handlers[response.command]) {
        // console.log("Command: " + response.command);
        // console.log(`Is valid command: ${!!this.handlers[response.command]}`);
        // Directly pass validated and structured data to the component
        this.handlers[response.command](
          this.structureDataBasedOnCommand(response)
        );

        // // Notify about the error
        // this.notifyComponents(response.command, {
        //   rStatus: "error",
        //   rData: "Invalid response format for SocketCom"
        // });
      }
      // else {
      //   // can happen if the listener is not initialized at the time of the response
      //   console.error(
      //     "can happen if the listener is not initialized at the time of the response"
      //   );
      //   console.error("Unhandled command or invalid response", response);
      //   console.error("Command: " + response.command);
      //   console.error(`Is valid command: ${!!this.handlers[response.command]}`);
      // }
    });
  }

  // Connects to the server
  private connect(): void {
    SocketController.socket.on("connect", () => {
      console.log("Socket connected");
      this.socketStatus = "connected";
      if (this.userBidToken && this.userNickName) {
        this.authenticate(this.userNickName, this.userBidToken);
      }
      if (this.reconnectAttempts > 0) {
        this.reconnectAttempts = 0;
      }
    });

    SocketController.socket.on("disconnect", () => {
      console.log("Disconnected");
      this.attemptReconnect();
    });
  }

  private firstDisconnectTime: number = 0;

  private attemptReconnect() {
    if (this.firstDisconnectTime === 0) {
      this.firstDisconnectTime = Date.now();
    }
    if (this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
      setTimeout(
        () => {
          console.log(
            `Attempting to reconnect (${this.reconnectAttempts + 1}/${
              this.MAX_RECONNECT_ATTEMPTS
            })`
          );
          SocketController.socket.connect();
          this.reconnectAttempts++;
          // calculate time since first disconnect in seconds
          const timeSinceFirstDisconnect =
            (Date.now() - this.firstDisconnectTime) / 1000;
          console.log(
            "Time since first disconnect: " + timeSinceFirstDisconnect
          );
        },
        1000 * Math.pow(2, this.reconnectAttempts)
      ); // Exponential backoff strategy
    } else {
      console.log(
        "Max reconnect attempts reached. Please check your connection and try again."
      );
      // Notify the user or take additional steps
    }
  }

  public get status(): "connected" | "connecting" | "disconnected" {
    console.log(SocketController.socket);
    console.log("Socket connected: " + SocketController.socket.connected);
    console.log("Socket status: " + this.socketStatus);
    return this.socketStatus;
  }

  // General method to send commands with validation. Private as it should not be called directly
  private async sendCommand(
    command: SocketCom["command"],
    data: object = {},
    requireResponse: boolean
  ): Promise<SocketCom> {
    return new Promise((resolve, reject) => {
      const lang = getUserLanguage();
      data = { ...data, lanIso: lang };

      // Set a timeout to reject the promise if not resolved within TIMEOUT_DURATION
      let requestTimeout = setTimeout(() => {
        console.log("Request timed out for command " + command);

        Swal.fire({
          icon: "error",
          title: `Network Timeout`,
          html:
            `<strong class="font-extrabold">Please check your internet connection<br />and try reloading the page</strong>` +
            systemErrorAlertText(),
          // reload page button
          showConfirmButton: true,
          confirmButtonText: "Reload",
          customClass: {
            popup: "!rounded-lg",
            confirmButton:
              "!bg-secondary !outline-none !border-none !shadow-none"
          }
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.reload();
          }
        });

        resolve({ command, status: 0, data: "Request timed out" });
      }, this.TIMEOUT_DURATION);
      console.log("Sending command: " + command);
      console.log("Sending data:");
      console.log(data);
      // Basic validation for data structure can be added here if needed
      if (requireResponse) {
        SocketController.socket.emit(
          "messages",
          { command, data },
          (response: SocketCom) => {
            console.log(response);
            if (response.status && typeof response.status === "number") {
              clearTimeout(requestTimeout);
              resolve(response);
            } else {
              clearTimeout(requestTimeout);
              reject(new Error("Error with command " + command));
            }
          }
        );
      } else {
        SocketController.socket.emit("messages", { command, data });
        clearTimeout(requestTimeout);
        resolve({ command, status: 202, data: "Command sent" });
      }
    });
  }

  private structureDataBasedOnCommand(response: SocketCom): {
    rStatus: "success" | "error";
    rData: any; // validated and structured data
  } {
    // This method should structure the data according to the command
    // For example, for "last-messages", it might extract and format message data
    // Placeholder implementation:
    // console.log(response.command);
    if (response.command === "placed-bid") {
      if (validateFullPlacedBid(response)) {
        return {
          rStatus: "success",
          rData: response.data
        };
      } else {
        return {
          rStatus: "error",
          rData: "Invalid response format"
        };
      }
    } else if (response.command === "new-bids-count") {
      // console.log(response);
      if (validateNewBidsCount(response)) {
        // console.log(response);
        return {
          rStatus: "success",
          rData: {
            numBids: response.data.bids
          }
        };
      } else {
        console.log(response);
        return {
          rStatus: "error",
          rData: "Invalid response format"
        };
      }
    } else if (response.command === "new-auction-state") {
      console.log(response);
      if (validateNewAuctionStatus(response)) {
        console.log(response);
        return {
          rStatus: "success",
          rData: response.data
        };
      } else {
        console.log(response);
        return {
          rStatus: "error",
          rData: "Invalid response format"
        };
      }
    } else if (response.command === "favorite-product") {
      // console.log(response);
      if (validateFavoriteProduct(response)) {
        // console.log(response);
        return {
          rStatus: "success",
          rData: response.data
        };
      } else {
        console.log(response);
        return {
          rStatus: "error",
          rData: "Invalid response format"
        };
      }
    } else if (response.command === "new-reward") {
      console.log(validateNewReward(response));
      if (validateNewReward(response)) {
        console.log(response);
        return {
          rStatus: "success",
          rData: response.data.reward
        };
      } else {
        console.log(response);
        return {
          rStatus: "error",
          rData: "Invalid response format"
        };
      }
    } else if (response.command === "latest-bid") {
      if (validateLatestBid(response)) {
        return {
          rStatus: "success",
          rData: response.data
        };
      } else {
        console.log(response);
        return {
          rStatus: "error",
          rData: "Invalid response format"
        };
      }
    } else if (response.command === "add-to-cart") {
      if (validateAddToCart(response)) {
        return {
          rStatus: "success",
          rData: response.data
        };
      } else {
        console.log(response);
        return {
          rStatus: "error",
          rData: "Invalid response format"
        };
      }
    } else if (response.command === "won-auction") {
      if (validateWonAuction(response)) {
        return {
          rStatus: "success",
          rData: response.data
        };
      } else {
        console.log(response);
        return {
          rStatus: "error",
          rData: "Invalid response format"
        };
      }
    }

    console.log(response);

    return {
      rStatus: "error",
      rData: "Invalid command"
    };
  }

  // Method to notify components based on the command
  private notifyComponents(command: string, data: any) {
    if (this.handlers[command]) {
      this.handlers[command](data);
    }
  }

  public on(command: string, handler: (result: any) => void): void {
    this.handlers[command] = handler;
  }

  public off(command: string): void {
    delete this.handlers[command];
  }

  // Authentication
  public async authenticate(
    nickname: string,
    bidToken: string
  ): Promise<{
    rStatus: "success" | "error";
    rData: string;
  }> {
    // console.log("Authenticating");
    if (!nickname || !bidToken) {
      return Promise.reject(new Error("nickname and bidToken are required"));
    }

    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      return Promise.reject(new Error("Refresh token is required"));
    }

    console.log(`Sending auth command`);
    try {
      // response
      const response = await this.sendCommand(
        "auth",
        { nickname: nickname, bidToken: bidToken, refreshToken },
        true
      );

      console.log(response);

      // this.socket.emit(
      //   "messages",
      //   { command: "test-return", data: { productID: 1, a: "fromFE" } },
      //   (response: SocketCom) => {
      //     console.log(response);
      //   }
      // );

      if (response.status === 200) {
        this.userNickName = nickname;
        this.userBidToken = bidToken;

        if (
          !response ||
          !response.data ||
          typeof response.data !== "object" ||
          !("serverTime" in response.data) ||
          !response.data.serverTime ||
          typeof response.data.serverTime !== "number"
        ) {
          return { rStatus: "error", rData: "Authentication data missing" };
        }
        const timeSynced = checkTimeSync(response.data.serverTime);
        // console.log(timeSynced);
        if (!timeSynced) {
          Swal.fire({
            icon: "error",
            title: `Time not synced`,
            html:
              `<strong class="font-extrabold">Please check your device time settings</strong>` +
              systemErrorAlertText(),
            showConfirmButton: true,
            customClass: {
              popup: "!rounded-lg",
              confirmButton:
                "!bg-primary !outline-none !border-none !shadow-none"
            }
          });
          utilLogOut();
          window.location.replace("/help/time");
          return { rStatus: "error", rData: "Time not synced" };
        }

        return { rStatus: "success", rData: "Authenticated" };
      }

      if (response.status === 0) {
        return { rStatus: "error", rData: "Network error" };
      }

      return {
        rStatus: "error",
        rData: "Authentication failed"
      };
    } catch (error: unknown) {
      console.log(error);
      return { rStatus: "error", rData: "Authentication failed" };
    }
  }

  public async sendBid(productID: number): Promise<{
    rStatus: "success" | "error";
    rData: string;
  }> {
    if (!productID) {
      return Promise.reject(new Error("productID is required"));
    }

    console.log(`Sending create-bid command`);
    try {
      // response
      const response = await this.sendCommand(
        "create-bid",
        {
          productID: productID
        },
        true
      );

      console.log(response);

      // this.socket.emit(
      //   "messages",
      //   { command: "test-return", data: { productID: 1, a: "fromFE" } },
      //   (response: SocketCom) => {
      //     console.log(response);
      //   }
      // );

      if (response.status === 200 || response.status === 202) {
        return { rStatus: "success", rData: "Authenticated" };
      }

      if (response.status === 0) {
        return { rStatus: "error", rData: "Network error" };
      }

      return {
        rStatus: "error",
        rData: "Sending Bid failed"
      };
    } catch (error: unknown) {
      console.log(error);
      return { rStatus: "error", rData: "Sending Bid failed" };
    }
  }

  public async sendFav(
    productID: number,
    fav: boolean
  ): Promise<{
    rStatus: "success" | "error";
  }> {
    if (!productID) {
      return Promise.reject(new Error("productID is required"));
    }

    if (fav === null || fav === undefined) {
      return Promise.reject(new Error("fav is required"));
    }

    console.log(`Sending send-fav command`);
    try {
      // response
      const response = await this.sendCommand(
        "new-favorite-product",
        {
          productID: productID,
          favorite: fav
        },
        false
      );

      console.log(response);

      if (response.status === 200 || response.status === 202) {
        return { rStatus: "success" };
      }

      if (response.status === 0) {
        return { rStatus: "error" };
      }

      return {
        rStatus: "error"
      };
    } catch (error: unknown) {
      console.log(error);
      return { rStatus: "error" };
    }
  }

  public async sendTestEmit(data: object): Promise<{
    rStatus: "success" | "error";
    rData: string;
  }> {
    // if (!productID) {
    //   return Promise.reject(new Error("productID is required"));
    // }

    console.log(`Sending test-emit command`);
    try {
      // response
      const response = await this.sendCommand("test-emit", data, false);

      console.log(response);

      // this.socket.emit(
      //   "messages",
      //   { command: "test-return", data: { productID: 1, a: "fromFE" } },
      //   (response: SocketCom) => {
      //     console.log(response);
      //   }
      // );

      if (response.status === 200 || response.status === 202) {
        return { rStatus: "success", rData: "Authenticated" };
      }

      if (response.status === 0) {
        return { rStatus: "error", rData: "Network error" };
      }

      return {
        rStatus: "error",
        rData: "Sending Bid failed"
      };
    } catch (error: unknown) {
      console.log(error);
      return { rStatus: "error", rData: "Sending Bid failed" };
    }
  }

  public async sendUpdateCart(dataToSend: AddToCartData): Promise<{
    rStatus: "success" | "error";
    rData: string;
  }> {
    console.log(`Sending add-to-cart command`);
    try {
      // response
      const response = await this.sendCommand("add-to-cart", dataToSend, true);

      console.log(response);

      if (response.status === 200 || response.status === 202) {
        return { rStatus: "success", rData: "Authenticated" };
      }

      if (response.status === 0) {
        return { rStatus: "error", rData: "Network error" };
      }

      return {
        rStatus: "error",
        rData: "Sending add-to-cart failed"
      };
    } catch (error: unknown) {
      console.log(error);
      return { rStatus: "error", rData: "Sending add-to-cart failed" };
    }
  }

  // Disconnect the socket
  public disconnect(): void {
    SocketController.socket.disconnect();
  }
}

export default SocketController;
